import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import { fetchWithAuth } from '../../utils/api';
import { useMessages } from '../../components/Messages';
import QuoteConversion from './QuoteConversion';

function QuoteConversionPage({ quoteId, onBack }) {
  const { t } = useTranslation('sales');
  const { goToSalesOrder } = useAppNavigate();
  const { showSuccess, showError } = useMessages();
  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchQuotation();
  }, [quoteId]);

  const fetchQuotation = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetchWithAuth(`/api/quotes/${quoteId}`);
      
      if (response.ok) {
        const data = await response.json();
        setQuotation(data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || t('quotations.view.error.fetchFailed'));
      }
    } catch (err) {
      console.error('Error fetching quotation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConversionComplete = (result) => {
    showSuccess(
      t('convertQuoteToProduction.toasts.quoteConverted'),
      t('quoteConversionPage.convertedMessage', { number: result.so_number })
    );
    goToSalesOrder(result.sales_order_id);
  };

  const handleClose = () => {
    if (onBack) onBack();
  };

  if (loading) {
    return (
      <div className="view-container">
        <div className="loading">{t('quotations.view.loading')}</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="view-container">
        <div className="error-message">
          <h3>{t('quoteConversionPage.errorLoading')}</h3>
          <p>{error}</p>
          <div className="form-actions">
            <button className="btn-primary" onClick={fetchQuotation}>
              {t('common:ui.tryAgain')}
            </button>
            <button className="btn-secondary" onClick={handleClose}>
              {t('quoteConversionPage.goBack')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!quotation) {
    return (
      <div className="view-container">
        <div className="error-message">
          <h3>{t('quoteConversionPage.notFound')}</h3>
          <p>{t('quoteConversionPage.notFoundMessage')}</p>
          <button className="btn-secondary" onClick={handleClose}>
            {t('quoteConversionPage.goBack')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="view-container quote-conversion-page">
      <div className="view-header">
        <div className="header-title">
          <button className="btn-back" onClick={handleClose}>←</button>
          <h1>{t('enhancedQuoteConversion.title')}</h1>
          <span className="quote-number">{quotation.quotation_number}</span>
          <span className="customer-name">{quotation.customer_name}</span>
        </div>
      </div>

      <div className="view-content">
        <QuoteConversion
          quotation={quotation}
          onClose={handleClose}
          onConversionComplete={handleConversionComplete}
        />
      </div>
    </div>
  );
}

export default QuoteConversionPage;